import React, { useRef, useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import "./card.css";
import { TextCss } from "../../components/GlobalCss/TextCss";

// Extra breathing room added to the measured content height so the open
// animation doesn't clip descenders/shadows right at the edge.
const CONTENT_PADDING_BUFFER_PX = 20;

export function Accordion({ children, className = "" }) {
  return <div className={`custom-accordion ${className}`}>{children}</div>;
}

export function AccordionSection({ title, children, isOpen = false, isDisabled = false, onToggle }) {
  const contentRef = useRef(null);
  const contentInnerRef = useRef(null);

  // State to handle the transition phase regarding overflow
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Opening Logic
      if (contentRef.current && contentInnerRef.current) {
        // Already fully open (height:auto) — leave alone so dynamic content
        // (e.g. uploaded PDF preview) can grow without re-locking overflow:hidden.
        if (contentRef.current.style.height === "auto") {
          return;
        }

        const contentHeight = contentInnerRef.current.scrollHeight;

        // 1. Set explicit height for animation to start
        contentRef.current.style.height = `${contentHeight + CONTENT_PADDING_BUFFER_PX}px`;

        // Reset animation complete status
        setIsAnimationComplete(false);
      }
    } else {
      // Closing Logic
      if (contentRef.current && contentInnerRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        contentRef.current.style.height = `${contentHeight}px`;
        contentRef.current.offsetHeight;
        contentRef.current.style.height = '0px';

        setIsAnimationComplete(false);
      }
    }
  }, [isOpen, children]); // Trigger on Open/Close or if children change completely

  // While opening with a fixed height, keep the wrapper in sync if inner
  // content grows (e.g. PDF file chip appears before height becomes auto).
  useEffect(() => {
    if (!isOpen || !contentRef.current || !contentInnerRef.current) return;

    const wrapper = contentRef.current;
    const inner = contentInnerRef.current;

    const syncFixedHeight = () => {
      if (wrapper.style.height === "auto" || wrapper.style.height === "0px") return;
      const next = `${inner.scrollHeight + CONTENT_PADDING_BUFFER_PX}px`;
      if (wrapper.style.height !== next) {
        wrapper.style.height = next;
      }
    };

    syncFixedHeight();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(syncFixedHeight);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [isOpen]);

  // This handles the end of the CSS transition
  const handleTransitionEnd = () => {
    if (isOpen && contentRef.current) {
      // Animation finished opening:
      // Set height to auto so content can resize naturally
      contentRef.current.style.height = 'auto';
      // Mark animation as complete to enable visible overflow
      setIsAnimationComplete(true);
    }
  };

  const handleToggle = useCallback(() => {
    if (!isDisabled) {
      onToggle();
    }
  }, [isDisabled, onToggle]);

  return (
    <div className={`accordion-section ${isOpen ? "open" : ""}`}>
      <button
        type="button"
        disabled={isDisabled}
        className="accordion-header"
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <p className={isDisabled ? TextCss.grey_neutral.sub_heading_600_14 : TextCss.black.sub_heading_600_14}>{title}</p>
        <span className="accordion-icon ">
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>

      <div
        className="accordion-content-wrapper"
        ref={contentRef}
        onTransitionEnd={handleTransitionEnd}
        style={{
          // Key change: If open and animation done, show overflow. Otherwise hide it for animation.
          overflow: isOpen && isAnimationComplete ? 'visible' : 'hidden',
          transition: 'height 0.3s ease-out',
        }}
      >
        <div className="accordion-content" ref={contentInnerRef}>
          {children}
        </div>
      </div>
    </div>
  );
}
